import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { Modal, IconButton, Stepper, Step, StepLabel, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import StreamSettings from './StreamSettings';
import StreamDefaultSettings from './StreamDefaultSettings';
import StreamComplete from './StreamComplete';
// import api from '@utils/axiosInstance';
import '@resources/assets/styles/_header.scss';

const steps = ['settings', 'default', 'complete'];

// Utility function to format file size
const formatFileSize = (size) => {
    if (size >= 1024 * 1024 * 1024) {
        return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    } else if (size >= 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    } else if (size >= 1024) {
        return `${(size / 1024).toFixed(2)} KB`;
    } else {
        return `${size} Bytes`;
    }
}

function VodStreamModal({ modalOpen, setModalOpen, url, idx }) {
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const [videoUrls, setVideoUrls] = useState([]);
    const [progress, setProgress] = useState([]);
    const [status, setStatus] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [activeStep, setActiveStep] = useState(0);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    useEffect(() => {
        if (modalOpen) {
            setProgress([{
                id: 0,
                percentage: 0,
            },
            {
                id: 1,
                percentage: 0,
            },
            {
                id: 2,
                percentage: 0,
            },
            {
                id: 3,
                percentage: 0,
            },
            {
                id: 4,
                percentage: 0,
            }])
        } else {
            setFiles([]);
            setUploadedFiles([]);
            setProgress([]);
        }
    }, [modalOpen])

    const onDrop = useCallback((acceptedFiles) => {
        setFiles(acceptedFiles);
        // const newFiles = acceptedFiles.map((file, idx) => {
        //     const videoUrl = URL.createObjectURL(file instanceof File ? file : new Blob([file]));

        //     return {
        //         id: idx,
        //         name: file.path,
        //         size: file.size,
        //         blob: file,
        //         videoUrl: videoUrl
        //     };
        // });
        // setUploadedFiles(prev => [...prev, ...newFiles]);
        setVideoUrls(prev => [...prev, ...acceptedFiles.map(file => URL.createObjectURL(file instanceof File ? file : new Blob([file])))]);
    }, [])

    useEffect(() => {
        console.log(progress)
    }, [progress])

    const onUpload = () => {
        files.map((file, idx) => {
            const formData = new FormData();

            const xhr = new XMLHttpRequest();

            // 업로드 진행률 업데이트
            xhr.upload.onprogress = (event) => {
                const percentage = parseInt((event.loaded / event.total) * 100);
                setProgress(prev => {
                    const updatedProgress = prev.map(p => p.id === idx ? { ...p, percentage: percentage } : p);
                    return updatedProgress;
                });
            };

            // 요청 상태 처리
            xhr.onreadystatechange = () => {
                if (xhr.readyState !== 4) return;
                if (xhr.status === 200) {
                    setStatus('success');
                } else {
                    setStatus('error');
                }
            };

            // 서버로 파일 전송
            xhr.open('POST', 'https://httpbin.org/post', true);
            xhr.send(formData);
        });

        const allFilesUploaded = progress.every(p => p.percentage === 100);
        if (allFilesUploaded) {
            setProgress(prev => prev.map(p => ({ ...p, percentage: 0 })));
        }
    };

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop,
        multiple: true,
        // parallelUploads: 5,
        accept: {
            "video/*": [],
        }
    })

    const RootProps = {
        ...getRootProps(),
    };

    useEffect(() => () => {
        // createObjectURL 로 생성한 URL을 revokeObjectURL 로 제거 해준다
        // 메모리 누수를 방지하기 위해 생성된 URL을 DOM과 바인딩한 후에는 해제하는 것이 좋다고한다.
        files.forEach(file => URL.revokeObjectURL(file.preview));
    }, [files]);

    //현재 업로드한 이미지 파일을 취소하는 기능
    const handleCancle = () => {
        console.log('cancle')
        setFiles([])
    }

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        }
    };

    return (
        <Modal
            className="video-upload-modal"
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            disablePortal
        >
            <div className="modal-container">
                <div className="modal-header">
                    <h1>{t('header.stream_modal.create_stream')}</h1>
                    <IconButton className="close-button" onClick={() => setModalOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <div className="modal-body">
                    <Stepper activeStep={activeStep} alternativeLabel style={{ width: '100%', marginBottom: '20px' }}>
                        {steps.map((step) => (
                            <Step key={step}>
                                <StepLabel>{t(`header.stream_modal.${step}`)}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    <div style={{ width: '100%', height: 500, maxHeight: 800, overflow: 'auto' }}>
                        {activeStep === 0 && <StreamSettings />}
                        {activeStep === 1 && <StreamDefaultSettings />}
                        {activeStep === 2 && <StreamComplete />}
                    </div>
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #ddd', paddingTop: '20px' }}>
                    {
                        activeStep > 0 &&
                        <Button variant="outlined" onClick={handleBack} disabled={activeStep === 0} style={{ borderRadius: '50px', marginRight: '10px' }}>
                            {t('header.stream_modal.back')}
                        </Button>
                    }
                    <Button variant="contained" onClick={handleNext} disabled={activeStep === steps.length - 1} style={{ borderRadius: '50px' }}>
                        {t('header.stream_modal.next')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

export default VodStreamModal;
